/**
 * GET /event/state — public endpoint that exposes the current event phase
 * + active week. Source of truth for "should the leaderboard render" and
 * "which week is the default tab".
 *
 * See api/src/modules/event/event.controller.ts:getState for the
 * authoritative response shape.
 */
import { apiJson } from '../api';

export type EventPhase = 'PRE_EVENT' | 'IN_EVENT' | 'POST_EVENT';

export interface EventStateResponse {
  status: number;
  data: {
    phase: EventPhase;
    /** 1..5 — derived server-side from event_weeks. */
    currentWeek: number;
    /** When the next phase/week transition fires; null when there isn't one
     * (POST_EVENT, or no weeks seeded). */
    nextTransitionAt: string | null;
    serverTime: string;
  };
}

export async function getEventState(init?: RequestInit): Promise<EventStateResponse['data']> {
  const res = await apiJson<EventStateResponse>('/event/state', init);
  return res.data;
}

export interface EventWeekRules {
  version: string;
  title: string;
  slug: string;
  effectiveFrom: string;
  /** When false, /rules renders a "come back later" placeholder for this
   *  week instead of the rules body. Tab visibility is unaffected. Ops
   *  flips this to true (one UPDATE row in MySQL) once legal signs off. */
  published: boolean;
}

export interface EventWeek {
  weekNumber: number;
  airAt: string;
  playCutoffAt: string;
  rules: EventWeekRules | null;
}

export interface EventWeeksResponse {
  status: number;
  data: {
    config: {
      eventId: string;
      preEventActiveStart: string;
      postEventStart: string;
      displayTimezone: string;
    };
    weeks: EventWeek[];
  };
}

/**
 * GET /event/weeks — full event schedule + per-week rules version. The
 * registration UI uses it to discover the current week's `rulesVersion`
 * before submitting POST /auth/register.
 */
export async function getEventWeeks(init?: RequestInit): Promise<EventWeeksResponse['data']> {
  const res = await apiJson<EventWeeksResponse>('/event/weeks', init);
  return res.data;
}

export interface MyConsentResponse {
  status: number;
  data: {
    hasConsent: boolean;
    version: string | null;
  };
}

/**
 * GET /event/rules/consent/me?weekNumber=N — has-consent check against the
 * CURRENT rules version. A stale consent (against a superseded version)
 * counts as no consent, so the SPA always trusts a `false` and prompts.
 */
export async function getMyWeeklyConsent(
  weekNumber: number,
  init?: RequestInit,
): Promise<MyConsentResponse['data']> {
  const qs = new URLSearchParams({ weekNumber: String(weekNumber) });
  const res = await apiJson<MyConsentResponse>(`/event/rules/consent/me?${qs.toString()}`, init);
  return res.data;
}

export interface PostConsentBody {
  weekNumber: number;
  rulesVersion: string;
  source: 'registration' | 'play-gate';
  clientTs?: string;
}

/**
 * POST /event/rules/consent — records the user's agreement to the named
 * (weekNumber, rulesVersion). Idempotent on the unique key, so a duplicate
 * submit is a 200.
 */
export async function postWeeklyConsent(body: PostConsentBody): Promise<void> {
  await apiJson('/event/rules/consent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
